import { TextAttribute } from '@/services/shape/attributes/text';
import { buildAstState } from '..';
import {
  encodeHTMLEntities,
  generateResourceName,
  getRefName
} from '../utils/name.utils';
import { addResourceIfNotExists, insertImages } from '../utils/resource.utils';
import { Shape } from '@/services/shape';
import { toArgb } from '@/watch-renderer/utils/color';

const XmlOperatorsMap: {
  [key in Exclude<AnimationTrigger['whenOperator'], undefined>]: string;
} = {
  LessThan: '&lt;',
  LessThanOrEqual: '&lt;=',
  Equal: '==',
  MoreThan: '>',
  MoreThanOrEqual: '>=',
  Residual: '%'
};

const emptyImgWidgetAst = (resourceAst: Ast, shape?: Shape) => {
  if (!shape) return;
  let refName = '';

  // 优先检查是否有上传的空状态图片（所有类型通用）
  if (
    shape.attrs.resources?.iftttEmptyImage &&
    Object.keys(shape.attrs.resources.iftttEmptyImage).length !== 0
  ) {
    const imageMap = insertImages(
      resourceAst,
      {
        iftttEmptyImage: shape.attrs.resources?.iftttEmptyImage
      },
      {
        recolorEnable: true
      }
    );
    refName = getRefName(imageMap['iftttEmptyImage']);
  } else if (shape.type === 'Text') {
    const attrs = shape.attrs as TextAttribute;
    const {
      sizeContent,
      rotation,
      fontId = 'misans',
      fontSize,
      fontWeight = 'normal',
      color = '#ffffff',
      style,
      align,
      letterSpace,
      w = 0,
      h = 0,
      radius = 0,
      startAngle = 0,
      verticalAlign,
      reverseText,
      arcSpan = 45,
      longMode = 'dot',
      strings = []
    } = attrs;

    // 构建字符串：只替换数据源为 "--"，保留非数据源文本
    let stringWhole = '';

    strings.forEach((item, index) => {
      const { isSource, value = '' } = item;
      if (index == 0 && value == ' ') return;

      let string: string;
      // 非数据源：保留原始文本（需转义%）
      isSource ? (string = '--') : (string = value.replace('%', '%%'));

      if (string) {
        stringWhole += string;
      }
    });

    // 如果没有字符串，默认使用 "--"
    if (!stringWhole) {
      stringWhole = '--';
    }

    const refAst: Ast = {
      tag: 'DataItemText',
      attrsMap: {
        w: !sizeContent ? w + '' : '1023',
        h: !sizeContent ? h + '' : '1023',
        rotation: rotation + '',
        fontId: fontId.toLowerCase(),
        fontSize: String(fontSize),
        fontWeight: fontWeight.toLowerCase(),
        color: toArgb(color),
        style: style,
        align:
          style == 'arc'
            ? align ?? 'left'
            : sizeContent
            ? 'left'
            : align ?? 'left',
        letterSpace: String(letterSpace),
        longMode: longMode,
        string: stringWhole
      },
      children: []
    };
    if (style === 'arc') {
      const _reverseText = reverseText ? -1 : 1;
      refAst.attrsMap.radius = String(radius);

      // 起始角度
      refAst.attrsMap.startAngle = String(Number(startAngle) * _reverseText);
      // refAst.attrsMap.startAngle = String(startAngle);

      // 修改span值为用户设置
      refAst.attrsMap.span = String(Number(arcSpan) * _reverseText);

      // 垂直对齐属性，需要在此增加判断，因为部分表盘不支持此字段
      if (verticalAlign) {
        refAst.attrsMap.verticalAlign = verticalAlign;
      }
    }
    const ast = addResourceIfNotExists(resourceAst, refAst);
    refName = '@' + ast.attrsMap.name;
  } else {
    const imageAst = addResourceIfNotExists(resourceAst, {
      tag: 'Image',
      attrsMap: {
        src: '_emptyImage.png',
        compressMethod: 'None',
        format: 'indexed8'
      },
      children: []
    });
    refName = `@${imageAst.attrsMap.name}`;
  }

  const widgetAst = addResourceIfNotExists(
    resourceAst,
    {
      tag: 'Widget',
      attrsMap: {
        name: generateResourceName(resourceAst, 'Widget_EmptyImg')
      },
      children: [
        {
          tag: 'Item',
          attrsMap: {
            ref: refName
          },
          children: []
        }
      ]
    },
    undefined,
    true
  );
  return widgetAst;
};
const getParamAst = (
  resourceAst: Ast,
  param: CompareParam,
  type: Compare['type']
) => {
  const { id, whenOperator = 'MoreThan', whenSourceKey, whenValue } = param;

  const ast: Ast = {
    tag: 'Param',
    attrsMap: {
      name: `Param_${id}`
    },
    children: []
  };
  if (type === 'when') {
    ast.attrsMap.value = whenValue + '';
    ast.attrsMap.source = whenSourceKey + '';
    ast.attrsMap.operators = XmlOperatorsMap[whenOperator];
  } else {
    ast.attrsMap.value = 'short_clicked';
    ast.attrsMap.operators = XmlOperatorsMap.Equal;
  }
  return ast;
};
const getCompareAst = (resourceAst: Ast, compare: Compare) => {
  const { logicalOperator, type, children, id } = compare;
  const ast: Ast = {
    tag: 'Compare',
    attrsMap: {
      name: `Compare_${id}`,
      logic_op: logicalOperator || 'or',
      type: type === 'when' ? 'data' : 'objEvent'
    },
    children: children?.map((p) => getParamAst(resourceAst, p, type)) || []
  };
  return ast;
  // 生成参数
  // const paramChildren = Array.isArray(compares)
  //   ? compares.map((compare, index) =>
  //       generateParamAst(resourceAst, compare, index + 1)
  //     )
  //   : [generateParamAst(resourceAst, compares, 1)];

  // // 比较参数
  // const compare = Array.isArray(compares) ? compares[0] : compares;

  // const compareAst: Ast = {
  //   tag: 'Compare',
  //   attrsMap: {
  //     name: `Compare${index}`,
  //     logic_op: 'and',
  //     type: (['when'] as TriggerType[]).includes(compare.type)
  //       ? 'data'
  //       : 'objEvent'
  //   },
  //   children: paramChildren
  // };
  // return compareAst;
};

const layout2Widget = (resourceAst: Ast, layout: Ast) => {
  const widget: Ast = addResourceIfNotExists(resourceAst, {
    tag: 'Widget',
    attrsMap: {
      name: encodeHTMLEntities('Widget')
    },
    children: [
      {
        tag: 'Item',
        attrsMap: {
          ref: layout.attrsMap.ref,
          _id: layout.attrsMap._id,
          // 显示/隐藏动画继承父元素缩放/透明度，保证在画布中展示正确
          _alpha: layout.attrsMap._alpha,
          _scale: layout.attrsMap._scale
        },
        children: []
      }
    ]
  });
  return widget;
};

const bindSlotConditon = (
  resourceAst: Ast,
  layoutAst: Ast[],
  conditionAst: Ast,
  shapeLayoutAsts: Ast[],
  shape?: Shape
) => {
  const layouts = layoutAst.map((layout) => {
    const layoutWidget = layout2Widget(resourceAst, layout);
    const first_child = {
      tag: 'Item',
      attrsMap: {
        _tag: 'Condition',
        _name: 'ifttt-条件生效时显示的元素',
        ref: `@${layoutWidget.attrsMap.name}`,
        condition: `@${conditionAst.attrsMap.name}`,
        selectMethod: 'inOrder'
      },
      children: []
    };

    const emptyItemWidget = emptyImgWidgetAst(resourceAst, shape);
    const last_child = {
      tag: 'Item',
      attrsMap: {
        _tag: 'Condition',
        _name: 'ifttt-默认显示的空元素',
        ref: `@${emptyItemWidget?.attrsMap.name}`
      },
      children: []
    };

    // 找到当前theme中相关slot，不然会与其他样式弄混
    // 使用 _shapeId 来区分不同的图层，确保同一位置的不同图层不会共享同一个 slot
    const useRes = resourceAst.children.filter((res) => {
      if (res.tag !== 'Slot' || res.attrsMap.type !== 'condition') return false;
      return shapeLayoutAsts.some(
        (item) =>
          res.attrsMap.name === item.attrsMap.ref.slice(1) &&
          item.attrsMap._shapeId === layout.attrsMap._shapeId
      );
    });

    // 建立 layout 引用到坐标和 shapeId 的映射
    const ref2Coords = new Map<string, string>();
    shapeLayoutAsts.forEach((shapeLayout) => {
      const refName = shapeLayout.attrsMap.ref?.slice(1);
      if (!refName) return;
      // 使用 shapeId 来区分不同图层，避免同一xy坐标位置的不同图层共享 slot
      const key = `${shapeLayout.attrsMap.x},${shapeLayout.attrsMap.y},${shapeLayout.attrsMap._shapeId}`;
      ref2Coords.set(refName, key);
    });

    // 建立坐标到可复用 slot 的映射
    const coords2Slot = new Map<string, Ast>();
    useRes.forEach((s) => {
      const key = ref2Coords.get(s.attrsMap.name);
      if (key) coords2Slot.set(key, s);
    });

    // 当前布局坐标 key，包含 shapeId 以区分不同图层
    const currKey = `${layout.attrsMap.x},${layout.attrsMap.y},${layout.attrsMap._shapeId}`;
    let slot = coords2Slot.get(currKey);
    if (!slot) {
      slot = addResourceIfNotExists(resourceAst, {
        tag: 'Slot',
        attrsMap: {
          name: encodeHTMLEntities('Slot'),
          type: 'condition',
          compressMethod: 'RLEReversed',
          recolorEnable: 'false',
          _as: 'slot-condition-show'
        },
        // 默认带有一个空元素
        children: [last_child]
      });
      coords2Slot.set(currKey, slot);
    }

    // 将first_child添加到slot的children中，倒数第二个
    slot.children.splice(slot.children.length - 1, 0, first_child);

    layout.attrsMap.ref = `@${slot.attrsMap.name}`;
    return layout;
  });

  return layouts;
};

export const getConditionAst = (resourceAst: Ast, condition: Condition) => {
  const { logicalOperator, children } = condition;
  const ast: Ast = addResourceIfNotExists(resourceAst, {
    tag: 'Condition',
    attrsMap: {
      logic_op: logicalOperator || 'or'
    },
    children: children.map((item) => getCompareAst(resourceAst, item))
  });
  return ast;
};

export const getLayoutAstWithCondition = (
  resourceAst: Ast,
  conditionAst: Ast,
  layoutAst: Ast[],
  shapeLayoutAst: Ast[],
  shape?: Shape
) => {
  // const conditionAst = getConditionAst(resourceAst, shape);
  const layouts = bindSlotConditon(
    resourceAst,
    layoutAst,
    conditionAst,
    shapeLayoutAst,
    shape
  );
  return layouts;
};
